import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import type { WeightDistributionBucket } from '@/domain/smoltReport';
import { formatDateShortLocalized } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import { buildWeightDistributionSpec } from '../utils/smoltChartSpecs';

import styles from './SmoltWeightDistributionHistogram.module.scss';

/** Chart.js' own default proportions, which the histogram always had. */
const ASPECT_RATIO = 2;

interface SmoltWeightDistributionHistogramProps {
    distribution: WeightDistributionBucket[];
    /** `YYYY-MM-DD` the distribution is from, which may be before the delivery date. */
    day: string;
    smallestFish: number | null;
    largestFish: number | null;
    isLoading?: boolean;
}

export default function SmoltWeightDistributionHistogram(
    props: SmoltWeightDistributionHistogramProps
) {
    const { distribution, day, smallestFish, largestFish, isLoading = false } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const dayLabel = formatDateShortLocalized(day, locale);

    const grams = (value: number | null): string | null =>
        value != null ? formatNumberByLocale(value, locale, 0) : null;

    const spec = useMemo(
        () =>
            buildWeightDistributionSpec(
                distribution,
                t('smoltReport.detailView.distributionShareInInterval'),
                locale
            ),
        [distribution, t, locale]
    );
    const chart = useMetricChart(spec);

    return (
        <Card padding="lg" fullWidth={false} className={styles.distributionContainer}>
            <div className={styles.header}>
                <div className={styles.heading}>
                    <div className={styles.title}>
                        {t('smoltReport.detailView.distributionTitle')}
                    </div>
                    <div className={styles.subtitle}>
                        <span>{dayLabel}</span>
                        <span>{t('smoltReport.detailView.distributionAggregated')}</span>
                    </div>
                </div>
                <div className={styles.extremes}>
                    <FishExtreme
                        label={t('cageWeight.distribution.smallestFish')}
                        value={grams(smallestFish)}
                    />
                    <FishExtreme
                        label={t('cageWeight.distribution.largestFish')}
                        value={grams(largestFish)}
                    />
                </div>
            </div>
            <MetricChart
                kind="bar"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                aspectRatio={ASPECT_RATIO}
                isLoading={isLoading}
            />
        </Card>
    );
}

interface FishExtremeProps {
    label: string;
    /** Grams, formatted; null shows a dash. */
    value: string | null;
}

/**
 * The midpoint of the lightest or heaviest bucket holding fish, not a measured fish weight, so
 * the value says it is approximate as the cage's Weight distribution page does.
 */
function FishExtreme(props: FishExtremeProps) {
    const { label, value } = props;
    const { t } = useTranslation();
    return (
        <Card variant="tinted" padding="sm" fullWidth={false} className={styles.extreme}>
            <Caption as="div">{label}</Caption>
            <div className={styles.extremeValue}>
                {value != null && (
                    <span className={styles.extremeUnit}>
                        {t('cageWeight.distribution.approximately')}
                    </span>
                )}
                {value ?? '—'}
                {value != null && <span className={styles.extremeUnit}>g</span>}
            </div>
        </Card>
    );
}
